/**
 * Menu Data Structure for Individual and Organization Users
 * Based on SIDEBAR_MENU_STRUCTURE.md specification
 */

// Material-UI icons
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MapIcon from '@mui/icons-material/Map';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Custom icons
import DroneIcon from '../components/DroneIcon';
import OperationsIcon from '../components/OperationsIcon';

/**
 * Individual User Menu Structure
 * Non-logged in users: 4 items (Home, Login, Pricing Plans, Resources, Contact Support)
 * Logged-in users: 8+ items (adds Mission Log, Compliance, Settings, Pricing Plans, optional Upgrade banner)
 */
export const getIndividualMenu = (profile, organizationContext = {}) => {
  // Check if user is logged in
  const isAuthenticated = !!profile;

  // Check if user has a Part 107 subscription
  const hasIndividualSubscription = profile?.subscription?.name;
  const isPart107Plan = hasIndividualSubscription &&
    (profile.subscription.name.includes('Part 107') ||
      profile.subscription.name.includes('part 107'));

  // Check if user has dual subscriptions (both individual and organization)
  const hasDualSubscriptions = organizationContext?.hasIndividualSubscription &&
    organizationContext?.isOrganizationUser;

  // Show upgrade banner only for logged-in users with Part 107 individual plans
  const showUpgradeBanner = isPart107Plan;

  const menuItems = [
    {
      id: 'home',
      title: 'Home',
      path: '/',
      icon: <HomeIcon className="sidebar-icon" />,
      ariaLabel: 'Home - LAANC Operations Map',
      description: 'Access the LAANC operations map'
    },
  ];

  // Add authenticated-only menu items
  if (isAuthenticated) {
    menuItems.push({
      id: 'mission-log',
      title: 'Operations Log',
      path: '/missionlog',
      icon: <AssignmentIcon className="sidebar-icon" />,
      ariaLabel: 'Mission Log - View past operations',
      description: 'View your flight history and mission logs',
      hasSubmenu: true,
      submenu: [
        {
          id: 'mission-log-list',
          title: 'Operations Log',
          path: '/missionlog',
          ariaLabel: 'Operations Log - View list of all operations'
        },
        {
          id: 'mission-log-calendar',
          title: 'Calendar (Beta)',
          path: '/mission-calendar',
          ariaLabel: 'Calendar (Beta) - View operations on a calendar',
          icon: <CalendarMonthIcon className="sidebar-icon" style={{ fontSize: 16 }} />
        }
      ]
    });

    // Add Subscriptions menu item only for users with dual subscriptions
    if (hasDualSubscriptions) {
      menuItems.push({
        id: 'subscriptions',
        title: 'My Subscriptions',
        path: '/subscriptions',
        icon: <AccountBalanceWalletIcon className="sidebar-icon" />,
        ariaLabel: 'My Subscriptions - Manage all subscriptions',
        description: 'View and manage your individual and organization subscriptions'
      });
    }

    // Note: Compliance features are ORGANIZATION-ONLY (not available for individual users)
    // Settings menu removed from individual sidebar per user request

    // Add upgrade banner only for Part 107 users
    if (showUpgradeBanner) {
      menuItems.push({ type: 'divider' });
      menuItems.push({
        type: 'upgrade-banner',
        id: 'upgrade-organization',
        title: 'Upgrade to Organization',
        subtitle: 'Add team members & manage fleet',
        path: '/plans',
        ariaLabel: 'Upgrade to organization plan',
        description: 'Unlock team collaboration and fleet management features',
        icon: <BusinessCenterIcon className="upgrade-icon" />
      });
    }
  }

  // Add final divider and bottom menu items (shown for all users)
  menuItems.push({ type: 'divider' });

  // Add Login button for non-authenticated users
  if (!isAuthenticated) {
    menuItems.push({
      id: 'login',
      title: 'Login',
      type: 'login-button',
      ariaLabel: 'Login to your account',
      description: 'Sign in to access your account'
    });
    menuItems.push({ type: 'divider' });
  }

  // Pricing Plans - Show for all users (non-authenticated and authenticated)
  menuItems.push({
    id: 'pricing-plans',
    title: 'Pricing Plans',
    path: '/plans',
    icon: <WorkspacePremiumIcon className="sidebar-icon" />,
    ariaLabel: 'View pricing plans and subscriptions',
    description: 'Explore subscription plans and features'
  });

  menuItems.push({
    id: 'help-support',
    title: 'Help & Support',
    path: '/help',
    icon: <HelpCenterIcon className="sidebar-icon" />,
    ariaLabel: 'Help & Support - Guides and support',
    description: 'Access guides, documentation, and support',
    hasSubmenu: true,
    submenu: [
      {
        id: 'help-faqs',
        title: 'FAQs',
        path: '/help/faqs',
        ariaLabel: 'Frequently asked questions about LAANC and regulations'
      },
      {
        id: 'help-contact',
        title: 'Contact Support',
        path: '/contactus',
        ariaLabel: 'Contact support team'
      },
      {
        id: 'help-legal',
        title: 'Legal & Policies',
        path: '/resources',
        ariaLabel: 'Legal documents and policies'
      }
    ]
  });

  return menuItems;
};

/**
 * Organization User Menu Structure
 * 14+ items with fleet management and team features
 * Role-based visibility controlled by permissions
 */
export const getOrganizationMenu = (userRole, organization, subscriptionLimits, fleetUsage = {}) => {
  // Calculate usage display strings
  const droneCount = fleetUsage.droneCount ?? 0;
  const batteryCount = fleetUsage.batteryCount ?? 0;
  const payloadCount = fleetUsage.payloadCount ?? 0;
  const droneUsage = subscriptionLimits?.maxDrones
    ? `${droneCount}/${subscriptionLimits.maxDrones}`
    : '';
  const batteryUsage = subscriptionLimits?.maxBatteries
    ? `${batteryCount}/${subscriptionLimits.maxBatteries}`
    : '';
  const payloadUsage = subscriptionLimits?.maxPayloads
    ? `${payloadCount}/${subscriptionLimits.maxPayloads}`
    : '';
  const teamUsage = organization?.memberCount && subscriptionLimits?.maxUsers
    ? `${organization.memberCount}/${subscriptionLimits.maxUsers}`
    : '';

  return [
    {
      id: 'dashboard',
      title: 'Dashboard',
      path: '/dashboard',
      icon: <DashboardIcon />,
      ariaLabel: 'Dashboard - Fleet overview',
      description: 'View fleet status and team activity',
      permission: 'VIEW_DASHBOARD'
    },
    {
      id: 'activities',
      title: 'Activity Log',
      path: organization?.organizationId ? `/organization/${organization.organizationUuid}/activities` : '/dashboard',
      icon: <HistoryIcon />,
      ariaLabel: 'Activity Log - View all organization activities',
      description: 'Track all changes and activities across your organization',
      permission: 'VIEW_DASHBOARD'
    },
    {
      id: 'operations',
      title: 'Operations',
      path: '/operations',
      icon: <OperationsIcon />,
      ariaLabel: 'Operations - LAANC and flight operations',
      description: 'Manage flight operations and LAANC authorizations',
      hasSubmenu: true,
      permission: 'VIEW_OPERATIONS',
      submenu: [
        {
          id: 'operations-create',
          title: 'Create Operation (LAANC)',
          path: '/operations/create',
          ariaLabel: 'Create new LAANC operation',
          permission: 'CREATE_OPERATION'
        },
        {
          id: 'operations-missions',
          title: 'Operations Log',
          path: '/missionlog',
          ariaLabel: 'View active, scheduled, and historical missions',
          permission: 'VIEW_OPERATIONS'
        },
        {
          id: 'operations-calendar',
          title: 'Calendar (Beta)',
          path: '/mission-calendar',
          ariaLabel: 'Calendar (Beta) - View and manage operations on a calendar',
          icon: <CalendarMonthIcon className="sidebar-icon" style={{ fontSize: 16 }} />,
          permission: 'VIEW_CALENDAR' // All roles can view; editing restricted at page level (Owner/Admin/Manager only)
        },
        {
          id: 'operations-templates',
          title: 'Operation Templates',
          path: '/operations/templates',
          ariaLabel: 'Operation templates library',
          permission: 'VIEW_OPERATIONS'
        }
      ]
    },
    {
      id: 'fleet',
      title: 'Fleet Management',
      path: '/fleet',
      icon: <DroneIcon />,
      ariaLabel: 'Fleet Management - Drones, batteries, and payloads',
      description: 'Manage your drone fleet and equipment',
      hasSubmenu: true,
      permission: 'VIEW_FLEET',
      submenu: [
        {
          id: 'fleet-drones',
          title: `Drones ${droneUsage}`,
          path: '/fleet/drones',
          ariaLabel: `Drone inventory - ${droneUsage} used`,
          permission: 'VIEW_FLEET'
        },
        {
          id: 'fleet-batteries',
          title: `Batteries ${batteryUsage}`,
          path: '/fleet/batteries',
          ariaLabel: `Battery inventory - ${batteryUsage} used`,
          permission: 'VIEW_FLEET'
        },
        {
          id: 'fleet-payloads',
          title: `Payloads & Sensors ${payloadUsage}`,
          path: '/fleet/payloads',
          ariaLabel: `Payload and sensor inventory - ${payloadUsage} used`,
          permission: 'VIEW_FLEET'
        },
        {
          id: 'fleet-maintenance',
          title: 'Maintenance Schedule',
          path: '/fleet/maintenance-schedule',
          ariaLabel: 'Equipment maintenance schedules',
          permission: 'VIEW_FLEET'
        }
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance',
      path: '/compliance',
      icon: <SecurityIcon />,
      ariaLabel: 'Compliance - Licenses, checklists, and incidents',
      description: 'Manage compliance and safety requirements',
      hasSubmenu: true,
      permission: 'VIEW_COMPLIANCE',
      submenu: [
        {
          id: 'compliance-dashboard',
          title: 'Compliance Dashboard',
          path: '/compliance/dashboard',
          ariaLabel: 'Compliance metrics and overview',
          permission: 'VIEW_ANALYTICS' // Manager+ only (excludes Pilots and Viewers)
        },
        {
          id: 'compliance-licenses',
          title: 'Pilot Licenses',
          path: '/compliance/licenses',
          ariaLabel: 'Pilot licenses and certifications',
          permission: 'VIEW_PILOT_LICENSES' // Excludes Pilots (cannot view their own or others' licenses)
        },
        {
          id: 'compliance-checklists',
          title: 'Pre-flight Checklists',
          path: '/compliance/checklists',
          ariaLabel: 'Pre-flight safety checklists',
          permission: 'VIEW_COMPLIANCE'
        },
        {
          id: 'compliance-incidents',
          title: 'Incident Reports',
          path: '/compliance/incidents',
          ariaLabel: 'Incident and accident reports',
          permission: 'CREATE_INCIDENT' // Pilots can log incidents but only view their own
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics',
      path: '/analytics',
      icon: <AssessmentIcon />,
      ariaLabel: 'Analytics - Reports and insights',
      description: 'View flight analytics and utilization reports',
      hasSubmenu: true,
      permission: 'VIEW_ANALYTICS', // Owner, Admin, Manager only
      submenu: [
        {
          id: 'analytics-flights',
          title: 'Flight Analytics',
          path: '/analytics/flights',
          ariaLabel: 'Flight statistics and analytics',
          permission: 'VIEW_ANALYTICS'
        },
        {
          id: 'analytics-utilization',
          title: 'Utilization Reports',
          path: '/analytics/utilization',
          ariaLabel: 'Fleet utilization reports',
          permission: 'VIEW_ANALYTICS'
        },
        {
          id: 'analytics-costs',
          title: 'Cost Analysis',
          path: '/analytics/costs',
          ariaLabel: 'Operational cost analysis',
          permission: 'VIEW_ANALYTICS'
        },
        {
          id: 'analytics-flight-logs',
          title: 'Flight Logs',
          path: '/analytics/flight-logs',
          ariaLabel: 'Search and manage all flight logs',
          permission: 'VIEW_FLIGHT_LOGS' // Pilots can view own logs, Viewers can view all logs
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      path: '/settings',
      icon: <SettingsIcon />,
      ariaLabel: 'Settings - Organization and team settings',
      description: 'Manage organization settings and preferences',
      hasSubmenu: true,
      permission: 'VIEW_SETTINGS',
      submenu: [
        {
          id: 'settings-organization',
          title: 'Organization Settings',
          path: '/settings/organization',
          ariaLabel: 'Organization profile and settings',
          permission: 'MANAGE_ORGANIZATION' // Owner, Admin only
        },
        {
          id: 'settings-team',
          title: `Team Members ${teamUsage}`,
          path: '/settings/team',
          ariaLabel: `Team members - ${teamUsage}`,
          permission: 'VIEW_TEAM'
        },
        {
          id: 'settings-roles',
          title: 'Roles & Permissions',
          path: '/settings/roles',
          ariaLabel: 'Manage roles and permissions',
          permission: 'MANAGE_ROLES' // Owner, Admin only
        },
        {
          id: 'settings-billing',
          title: 'Billing & Subscription',
          path: '/settings/organization?tab=billing',
          ariaLabel: 'Billing and subscription management',
          permission: 'VIEW_BILLING' // Owner, Admin only
        },
        {
          id: 'settings-integrations',
          title: 'Integrations',
          path: '/settings/integrations',
          ariaLabel: 'Third-party integrations',
          permission: 'MANAGE_INTEGRATIONS' // Owner, Admin only
        }
      ]
    },
    { type: 'divider' },
    {
      id: 'help-support',
      title: 'Help & Support',
      path: '/help',
      icon: <HelpCenterIcon />,
      ariaLabel: 'Help & Support - Guides and support',
      description: 'Access guides, documentation, and support',
      hasSubmenu: true,
      submenu: [
        {
          id: 'help-getting-started',
          title: 'Getting Started',
          path: '/help/getting-started',
          ariaLabel: 'Getting started guide'
        },
        {
          id: 'help-guides',
          title: 'User Guides',
          path: '/help/guides',
          ariaLabel: 'User guides and documentation'
        },
        {
          id: 'help-faqs',
          title: 'FAQs',
          path: '/help/faqs',
          ariaLabel: 'Frequently asked questions about LAANC and regulations'
        },
        {
          id: 'help-contact',
          title: 'Contact Support',
          path: '/contactus',
          ariaLabel: 'Contact support team'
        },
        {
          id: 'help-legal',
          title: 'Legal & Policies',
          path: '/resources',
          ariaLabel: 'Legal documents and policies'
        }
      ]
    }
  ];
};

/**
 * Role-based permission mapping
 * Used to filter menu items based on user role
 */
export const ROLE_PERMISSIONS = {
  owner: [
    'VIEW_DASHBOARD',
    'VIEW_OPERATIONS',
    'CREATE_OPERATION',
    'VIEW_FLEET',
    'VIEW_COMPLIANCE',
    'VIEW_PILOT_LICENSES',
    'VIEW_ANALYTICS',
    'VIEW_FLIGHT_LOGS',
    'VIEW_SETTINGS',
    'MANAGE_ORGANIZATION',
    'VIEW_TEAM',
    'MANAGE_ROLES',
    'VIEW_BILLING',
    'MANAGE_INTEGRATIONS',
    'CREATE_INCIDENT',
    'VIEW_CALENDAR',   // Can view AND edit calendar
    'EDIT_CALENDAR'
  ],
  admin: [
    'VIEW_DASHBOARD',
    'VIEW_OPERATIONS',
    'CREATE_OPERATION',
    'VIEW_FLEET',
    'VIEW_COMPLIANCE',
    'VIEW_PILOT_LICENSES',
    'VIEW_ANALYTICS',
    'VIEW_FLIGHT_LOGS',
    'VIEW_SETTINGS',
    'MANAGE_ORGANIZATION',
    'VIEW_TEAM',
    'MANAGE_ROLES',
    'VIEW_BILLING',
    'CREATE_INCIDENT',
    'VIEW_CALENDAR',   // Can view AND edit calendar
    'EDIT_CALENDAR'
  ],
  manager: [
    'VIEW_DASHBOARD',
    'VIEW_OPERATIONS',
    'CREATE_OPERATION',
    'VIEW_FLEET',
    'VIEW_COMPLIANCE',
    'VIEW_PILOT_LICENSES',
    'VIEW_ANALYTICS',
    'VIEW_FLIGHT_LOGS',
    'VIEW_TEAM',
    'CREATE_INCIDENT',
    'VIEW_CALENDAR',   // Can view AND edit calendar
    'EDIT_CALENDAR'
  ],
  pilot: [
    'VIEW_OPERATIONS', // Own only
    'CREATE_OPERATION', // Own only
    'VIEW_FLEET', // Read-only
    'VIEW_COMPLIANCE', // Own only
    'VIEW_FLIGHT_LOGS', // Own only
    'CREATE_INCIDENT',
    'VIEW_CALENDAR'    // View only — cannot edit
  ],
  viewer: [
    'VIEW_DASHBOARD',
    'VIEW_OPERATIONS', // Read-only
    'VIEW_FLEET', // Read-only
    'VIEW_COMPLIANCE', // Read-only
    'VIEW_PILOT_LICENSES', // Read-only
    'VIEW_ANALYTICS', // All analytics features
    'VIEW_FLIGHT_LOGS', // All logs
    'VIEW_TEAM', // Read-only
    'VIEW_CALENDAR'    // View only — cannot edit
  ]
};

/**
 * Helper function to check if user has permission
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  const roleLower = userRole.toLowerCase();
  return ROLE_PERMISSIONS[roleLower]?.includes(permission) || false;
};
